/**
 * Equalizes product title heights within the same grid row across .product-grid
 * (PLP/search) and .resource-list--grid (recommendations, you-may-also-like).
 *
 * Per-row: titles sharing the same vertical offset get a shared min-height equal
 * to the tallest title in that row. Rows with a single item are skipped.
 * Heights reset before each measurement so layout changes are always respected.
 */

const GRID_SELECTOR = '.product-grid, .resource-list--grid, .resource-list__carousel';
const TITLE_SELECTOR = '.product-title-block';
const activeGrids = new WeakSet();
const registeredGrids = new WeakSet();
const observedGrids = new WeakSet();

function equalizeProductTitles() {
  document.querySelectorAll(GRID_SELECTOR).forEach((grid) => {
    if (!activeGrids.has(grid)) return;

    const titles = [...grid.querySelectorAll(TITLE_SELECTOR)];
    if (titles.length < 2) return;

    titles.forEach((t) => (t.style.minHeight = ''));

    const rows = new Map();
    titles.forEach((title) => {
      // Skip elements inside display:none containers (hidden--mobile / hidden--desktop)
      const rect = title.getBoundingClientRect();
      if (rect.height === 0) return;

      const key = Math.round(rect.top);
      if (!rows.has(key)) rows.set(key, []);
      rows.get(key).push({ title, height: rect.height });
    });

    rows.forEach((rowItems) => {
      if (rowItems.length < 2) return;
      const max = Math.ceil(Math.max(...rowItems.map((item) => item.height)));
      rowItems.forEach(({ title }) => (title.style.minHeight = `${max}px`));
    });
  });
}

let rafId = null;

function scheduleEqualize() {
  if (rafId !== null) return;
  rafId = requestAnimationFrame(() => {
    rafId = null;
    equalizeProductTitles();
  });
}

window.addEventListener('load', scheduleEqualize);

let resizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(scheduleEqualize, 150);
});

function elementContainsSelector(element, selector) {
  return element.matches(selector) || element.querySelector(selector);
}

function hasRelevantAddedNode(mutation) {
  return [...mutation.addedNodes].some((node) => {
    if (!(node instanceof Element)) return false;
    return elementContainsSelector(node, TITLE_SELECTOR);
  });
}

function observeGrid(grid) {
  if (observedGrids.has(grid)) return;
  observedGrids.add(grid);
  new MutationObserver((mutations) => {
    if (mutations.some((mutation) => mutation.type === 'characterData' || hasRelevantAddedNode(mutation))) {
      scheduleEqualize();
    }
  }).observe(grid, { childList: true, characterData: true, subtree: true });
}

function activateGrid(grid) {
  if (activeGrids.has(grid)) return;
  activeGrids.add(grid);
  observeGrid(grid);
  scheduleEqualize();
}

const viewportObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            viewportObserver.unobserve(entry.target);
            activateGrid(entry.target);
          });
        },
        { rootMargin: '600px 0px' }
      )
    : null;

function registerGrid(grid) {
  if (registeredGrids.has(grid)) return;
  registeredGrids.add(grid);

  if (viewportObserver) {
    viewportObserver.observe(grid);
    return;
  }

  activateGrid(grid);
}

document.querySelectorAll(GRID_SELECTOR).forEach(registerGrid);

// Watch for grids added dynamically (e.g. AJAX-loaded product recommendations)
new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;

      if (node.matches(GRID_SELECTOR)) {
        registerGrid(node);
      }

      node.querySelectorAll(GRID_SELECTOR).forEach((grid) => {
        registerGrid(grid);
      });
    }
  }
}).observe(document.body, { childList: true, subtree: true });
